import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { Projects } from '../../../projects/resources/Projects';
import { Views } from '../../../views/resources/Views';
import { DataAlerts } from '../../../dda/resources/DataAlerts';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { CommonErrorCode } from '../../../ts/helpers/ErrorCodes';
import { isCurrentUrlViz } from '../../../ts/helpers/UrlHelpers';
import { IDataAlertInfo } from '../../../ts/models/IDataAlertInfo';
import { IViewDetailInfo } from '../../../ts/models/IViewInfo';
import { IExplorableItemTypeInfo, INamedItemInfo } from '../../../ts/models/resourceInfoParts';
import { IViz, VizType } from '../../../ts/models/Viz';
import { IViewActions } from '../../../ts/ServerApi/types/IViewActions';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { VizService } from '../../../ts/services/VizService';
import { enterSpace, setSpaceInfoItem } from '../../actions/spaces/EnterSpaceAction';
import { setVizPath } from '../../actions/spaces/SetVizPathAction';
import { getAppStore } from '../../AppStore';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '../../content/breadcrumbs/BreadcrumbHelpers';
import { VizPathInitialState } from '../../reducers/VizPathReducer';
import { selectLayoutType } from '../../selectors/LayoutTypeSelector';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { selectVizPath } from '../../selectors/VizPathSelector';
import { ServiceLocator } from '../../ServiceLocator';
import { LayoutType } from '../../state/LayoutType';
import { SpaceInfo } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { RouterError, RouterErrorOwnProps } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '../SpaceTelemetry';
import { sendVisitAlertFromEmailTelemetry } from './VizSpaceTelemetry';
import { VizUrl } from './VizUrlHelpers';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { VizView } from './Viz';
import { vizWithBreadcrumbStyle, fillViewPort, vizStyle, vizClass } from '~src/style/VizViewerStyles';

/**
 * This class renders tbViz, which is the container for a viz.
 * VPC and the viz communicate via the url and the parameters set on it. For example, when the viz is done
 * loading it sends a message and we update the url to have the instance query parameter (:iid=#). A lot of
 * java E2E tests wait for this to know that the viz is ready for interaction.
 * tbViz also creates the viz url for the _iframe_ that holds the viz. For example, it adds a device parameter
 * just for the iframe so the viz knows which device-specific-dashboard to show.
 * When a user navigates to: http://localhost:4000/#/views/DataAlertsWorkbook/DSDDashboard?:editDataAlert=89&:original_view=y&:iid=17
 * The iframe url might be:
 *  http://localhost:4000/views/DataAlertsWorkbook/DSDDashboard?:embed=y&:showVizHome=n&:toolbar=top&%3AeditDataAlert=89&%3Aoriginal_view=y&%3Adevice=tablet&%3AopenAuthoringInTopWindow=true&%3AbrowserBackButtonUndo=true&%3AcommentingEnabled=true&%3AreloadOnCustomViewSave=true&%3AshowAppBanner=false&%3AisVizPortal=true&:apiID=host1#navType=0&navSrc=Opt
 * which specifies information about commenting, device, and more.
 * It's very delicate and some url parameters should cause a re-render of the whole viz page, and some should not.
 * That's why this class has so much code.
 */

export interface VizSpaceProviderOwnProps {
  readonly vizPathFromUrl: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
}

export interface VizSpaceProviderStateProps {
  readonly vizPath: string;
  readonly sessionSiteUrlName: string;
  readonly layoutType: LayoutType;
  readonly isRequestAccessEnabled: boolean;
}

export interface VizSpaceProviderDispatchProps {
  readonly setVizPath: (vizPath: string) => void;
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
}

export interface VizSpaceProviderState {
  readonly viewInfo: IViewDetailInfo;
  readonly errorCode: number;
  readonly hasRoutingError: boolean;  // routing error only!
  readonly errorVizPath: string;
  readonly hasDataAlertError: boolean;
  readonly dataAlertRouterErrorProps: RouterErrorOwnProps;
  readonly dataAlert: IDataAlertInfo;  // only if editing a data alert
}

const initialNoErrorState: VizSpaceProviderState = {
  viewInfo: undefined,
  errorCode: undefined,
  hasRoutingError: false,
  hasDataAlertError: false,
  dataAlertRouterErrorProps: undefined,
  errorVizPath: undefined,
  dataAlert: undefined
};

export interface IVizProperties {
  view?: IViewDetailInfo;
}

interface VizViewerProps {
  viz: IViz;
  vizProps: IVizProperties;
  allowedAction: IViewActions;
  dataAlert: IDataAlertInfo;
  dalert: IDataAlertInfo;
  firstViewPathOfPageLoad: IViewDetailInfo;

  // used to display a RouterError
  // for the 'dont-have-access' errors
  displayRoutingError: boolean;  // Viz-not-found, NOT data alerts errors!
  displayDataAlertRoutingError: boolean;

  updateVizProviderState: (newState: Partial<VizSpaceProviderState>) => void;
  onVizPathChange: (newPath: string) => void;
  onVizInstanceIdChange: (newInstanceId: string) => void;
  onVizIsExplicitOriginalVizChange: (isExplicitOriginalViz: boolean) => void;
  clearDataAlertRoute: () => void;
}


export type VizSpaceProviderProps = VizSpaceProviderOwnProps & VizSpaceProviderStateProps & VizSpaceProviderDispatchProps;

export class VizSpaceProvider extends React.Component<VizSpaceProviderProps, VizSpaceProviderState> {

  private vizViewProps: VizViewerProps = null;
  private unmounted: boolean = false;
  private viz: tb.IViz = undefined; // the actual viz object
  private vizContainer: HTMLElement; // the viz container
  private vizInitialized = false;
  private vizService = ServiceLocator.get<VizService>('VizService');
  private server = getSessionManagementServiceInstance();

  public constructor(props: VizSpaceProviderProps) {
    super(props);
    this.state = initialNoErrorState;
  }

  private renderVizContainer() {
    // this structure is intended to keep the same structure of the existing viz container 
    // changes to this will easily break tests
    return (
      <div style={vizWithBreadcrumbStyle} id={'viz-container'}>
        <div style={fillViewPort}>
          <div ref={c => this.vizContainer = c} style={vizStyle} className={vizClass} id={'viz'} tb-test-id={'viz'}> </div>
        </div>
      </div>);

  }

  private loadViz() {
    this.viz && this.viz.dispose();
    this.viz = VizView.loadViz(this.vizViewProps || this.initVizViewerProps(), this.vizContainer);
    this.vizInitialized = true;
  }

  public render() {
    const dataAlertIdFromUrl: string = VizUrl.getVizParams(this.props.routeLocationSearch)[UrlParamKeys.EditDataAlert];

    if (this.state.hasDataAlertError) {
      this.vizInitialized = false;
      return <RouterError {...this.state.dataAlertRouterErrorProps} />;

    } else if (this.state.hasRoutingError) {
      this.vizInitialized = false;
      if (this.props.isRequestAccessEnabled) {
        return <RequestAccessError
          url={this.props.urls.exploreUrl()}
          contentIdentifier={this.state.errorVizPath}
          contentType={'view'}
          errorCode={this.state.errorCode} />;
      }

      // old error page
      return <RouterError urls={this.props.urls} errorType="view" />;

    } else if (!this.state.viewInfo) {
      this.vizInitialized = false;
      return null;

    } else if (!_.isUndefined(dataAlertIdFromUrl) && (_.isUndefined(this.state.dataAlert) || this.state.dataAlert.id !== dataAlertIdFromUrl)) {
      this.vizInitialized = false;
      // don't render viz if we are editing a data alert until the data alert for the current data alert id (in the url)
      // is fetched, so that we don't render the wrong viz for the alert device type
      return null;
    }

    // have view info and no errors, render the viz
    if (!this.vizViewProps) {
      this.initVizViewerProps();
    }
    return this.renderVizContainer();
  }

  public async componentDidMount() {
    // set initial viz path
    this.props.setVizPath(this.props.vizPathFromUrl);
    const firstViewInfoOfPageLoad = await this.getViewInfoAndUpdateBrowserTitleAndGetDataAlert();
    if (firstViewInfoOfPageLoad) {
      // make sure to update breadcrumbs with initial view info
      this.updateBreadcrumbs(firstViewInfoOfPageLoad);
    }

    if (this.vizContainer && !this.vizInitialized) {
      this.loadViz();
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
    this.props.setVizPath(''); // reset store

    this.viz && this.viz.dispose();
  }

  private getViewInfoAndUpdateBrowserTitleAndGetDataAlert = async () => {
    try {
      const viewInfo: IViewDetailInfo = await Views.getByPath(this.props.vizPathFromUrl);
      BrowserTitleService.setVizTitle(viewInfo.name, viewInfo.workbook.name);
      this.props.setSpaceInfoItem({ item: viewInfo, spaceType: 'view', subspaceName: undefined });

      try {
        if (!_.isUndefined(viewInfo)) {
          const currDataAlertId = VizUrl.getVizParams(this.props.routeLocationSearch)[UrlParamKeys.EditDataAlert];
          if (currDataAlertId) {
            // data alert info needs to be fetched before we update the state and render the viz
            const dataAlert: IDataAlertInfo = await DataAlerts.getById(currDataAlertId);
            this.handleGetDataAlertResponse(dataAlert, viewInfo);
          } else {

            // not editDataAlert parameter so just set the viewInfo on the state
            this.updateState({ ...initialNoErrorState, viewInfo: viewInfo });
          }
        }
        return viewInfo;

      } catch (e) {
        this.handleGetDataAlertReturnsError(viewInfo);
        return viewInfo;
      }

    } catch (errorCode) {
      // fetching viewInfo failed-- show view routing error
      this.handleViewGetByPathError(errorCode,
        () => this.updateState({
          viewInfo: undefined,
          errorCode: errorCode,
          errorVizPath: this.props.vizPathFromUrl,
          hasRoutingError: true,
          hasDataAlertError: false,
          dataAlertRouterErrorProps: undefined,
          dataAlert: undefined
        }),
        this.props.vizPathFromUrl);
      return undefined;
    }
  }

  private getVizParamsThatRequireReload(routeLocationSearch: string): _.Dictionary<any> {
    let params = VizUrl.getVizParams(routeLocationSearch);
    params = _.omit(params, [UrlParamKeys.OriginalView]);
    return params;
  }

  public async componentDidUpdate(prevProps: VizSpaceProviderProps, prevState: VizSpaceProviderState) {
    const prevVizPathFromUrl = prevProps.vizPathFromUrl;
    const currVizPathFromUrl = this.props.vizPathFromUrl;
    const vizPathFromAppState = this.props.vizPath;

    let currVizParams = this.getVizParamsThatRequireReload(this.props.routeLocationSearch || '');
    let prevVizParams = this.getVizParamsThatRequireReload(prevProps.routeLocationSearch || '');
    const currInstanceId = VizUrl.getInstanceId(this.props.routeLocationSearch);
    const prevInstanceId = VizUrl.getInstanceId(prevProps.routeLocationSearch);

    // there was an instance id but now there isn't, reload the viz which will get an instance id
    const instanceIdWasRemoved = (!currInstanceId && !!prevInstanceId);

    const prevDataAlertId = VizUrl.getVizParams(prevProps.routeLocationSearch)[UrlParamKeys.EditDataAlert];
    const currDataAlertId = VizUrl.getVizParams(this.props.routeLocationSearch)[UrlParamKeys.EditDataAlert];

    if (prevDataAlertId !== currDataAlertId &&
      !_.isUndefined(this.state.viewInfo)  /** need to have a viewinfo to make sure the alert is for the right viz */) {
      if (!_.isUndefined(currDataAlertId)) {
        try {
          const dataAlert: IDataAlertInfo = await DataAlerts.getById(currDataAlertId);
          this.handleGetDataAlertResponse(dataAlert, this.state.viewInfo);
          return;
        } catch (e) {
          this.handleGetDataAlertReturnsError(this.state.viewInfo);
        }
      } else {
        // prevDataAlertId must be removed now so remove the data alert info from the state and continue to
        // rendering and fetching the viz information
        this.updateState({
          ...this.state,
          hasDataAlertError: false,
          dataAlert: undefined
        });
      }
    }

    // if the viz search parameters changed (except for instance id) OR an instance Id was removed,
    // reload the viz. We don't want to reload the viz every time the instance id changes completely because
    // that's what indicates that the viz loaded and this would cause an infinite loop.
    const vizParamsChanged = !_.isEqual(currVizParams, prevVizParams) || instanceIdWasRemoved;

    const dataAlertChanged = !_.isEqual(this.state.dataAlert, prevState.dataAlert);

    if ((prevVizPathFromUrl !== currVizPathFromUrl && currVizPathFromUrl !== vizPathFromAppState) || vizParamsChanged || dataAlertChanged) {

      // the url changed (because the app state vizPath is different from the new route props)
      // but the vizPath is not updated yet. So update it and reload the viz.
      // if we are here just because the vizParamsChanged, it doesn't hurt to set the viz path anyway
      this.props.setVizPath(currVizPathFromUrl);


      if (this.vizViewProps) {

        if (currVizPathFromUrl !== this.vizViewProps.viz.path) {
          this.vizViewProps.onVizPathChange(currVizPathFromUrl);
        }

        this.vizViewProps.viz = {
          path: currVizPathFromUrl,
          vizType: VizType.View,
          params: VizUrl.getVizParams(this.props.routeLocationSearch || ''),
          siteUrlName: this.server.sessionSiteUrlName(),
          instanceId: VizUrl.getInstanceId(this.props.routeLocationSearch),
          isExplicitOriginalViz: VizUrl.getIsExplicitOriginalVizFromUrl(window.location.href),
        };
        this.vizViewProps.displayRoutingError = this.state.hasRoutingError;
        this.vizViewProps.dataAlert = this.state.dataAlert;
        this.vizViewProps.displayDataAlertRoutingError = this.state.hasDataAlertError;
        this.vizViewProps.dalert = this.vizViewProps.dataAlert;

        if (!this.vizViewProps.displayDataAlertRoutingError && !this.vizViewProps.displayRoutingError) {
          this.loadViz();
        }
      } else {
        // since there is no spaceScope, that probably means we were showing the error page before.
        // fetch the view here, the data alert (if needed) and then keep going
        if (_.isUndefined(this.state.viewInfo)) {
          await this.getViewInfoAndUpdateBrowserTitleAndGetDataAlert();
        }
      }

    } else {
      if (
        !this.vizInitialized &&
        this.vizContainer &&
        !this.state.hasDataAlertError &&
        !this.state.hasRoutingError &&
        this.state.viewInfo) {
        // this handles the case that it shows error before and then url is fixed
        // in this case the viz will not be initialized in componentDidMount but here
        this.loadViz();
      }
    }
  }

  public shouldComponentUpdate(nextProps: VizSpaceProviderProps) {
    const nextVizPathFromUrl = nextProps.vizPathFromUrl;
    if (nextProps.vizPath !== VizPathInitialState &&
      nextProps.vizPath !== this.props.vizPath &&
      nextProps.vizPath !== nextVizPathFromUrl) {
      // vizPath changed via user clicking on viz tab (that's why the url viz path does not match the new app state vizPath-- see tbViz),
      // so don't rerender
      return false;
    }
    return true;
  }

  private initVizViewerProps = () => {
    const props = {} as VizViewerProps;
    props.updateVizProviderState = this.updateState;

    props.viz = {
      path: this.props.vizPathFromUrl,
      vizType: VizType.View,
      params: VizUrl.getVizParams(this.props.routeLocationSearch),
      siteUrlName: this.server.sessionSiteUrlName(),
      instanceId: VizUrl.getInstanceId(this.props.routeLocationSearch),
      isExplicitOriginalViz: VizUrl.getIsExplicitOriginalVizFromUrl(window.location.href)
    };

    props.displayRoutingError = this.state.hasRoutingError;
    props.displayDataAlertRoutingError = this.state.hasDataAlertError;
    props.dataAlert = this.state.dataAlert;

    props.vizProps = {};

    function displayInvalidVizError(errorCode: number, vizPath: string) {
      props.displayRoutingError = true;
      props.displayDataAlertRoutingError = false;

      props.updateVizProviderState({
        hasRoutingError: true,
        errorCode: errorCode,
        errorVizPath: vizPath,
        viewInfo: undefined,
        hasDataAlertError: false,
        dataAlertRouterErrorProps: undefined,
      });
    }

    props.onVizPathChange = (newPath: string) => {
      props.viz.path = newPath;

      if (!newPath) {
        displayInvalidVizError(CommonErrorCode.UnexpectedParameterValue, newPath);
        return;
      }
      const searchParams = WindowLocationService.getHashQueryObject();
      Views.getByPath(newPath)
        .then((viewInfo: IViewDetailInfo) => {
          this.handleNewViewInfoReact(viewInfo, props, searchParams);
        })
        .catch((errorCode: number) => {
          // B153202, TFSID: 463801 - If user does not have permission to view the viz but guest does
          // then we receive a GuestPermittedViewNotFoundError, so we can try to automatically
          // redirect to to the embedded view (if not a site or server admin). But if there's a regular
          // ViewNotFound error, there's no chance for guest to be able to view it, so just show the error here and now.
          this.handleViewGetByPathError(errorCode, displayInvalidVizError, newPath);
        });
    };

    props.onVizInstanceIdChange = (newInstanceId: string) => {

      if (newInstanceId && props.viz.instanceId !== newInstanceId) {
        props.viz.instanceId = newInstanceId;
        WindowLocationService.updateHashQueryParameterWithReplace(VizService.InstanceIdKey, newInstanceId, { encode: false });
      }
    };

    props.onVizIsExplicitOriginalVizChange = (isExplicitOriginalViz: boolean) => {
      props.viz.isExplicitOriginalViz = isExplicitOriginalViz;
      isExplicitOriginalViz ? WindowLocationService.updateHashQueryParameterWithReplace(VizService.ExplicitOriginalVizParamKey, 'y', { encode: false })
        : WindowLocationService.deleteQueryParameterWithReplace(VizService.ExplicitOriginalVizParamKey, { encode: false });
    };

    props.clearDataAlertRoute = () => {
      WindowLocationService.deleteQueryParameter(UrlParamKeys.EditDataAlert, { encode: false });
      WindowLocationService.deleteQueryParameter(UrlParamKeys.ResumeDataAlert, { encode: false });
    };

    this.vizViewProps = props;
    return props;
  }

  private handleViewGetByPathError(errorCode: number, displayInvalidVizError: (errorCode: number, vizPath: string) => void, newPath: string) {
    if (errorCode === CommonErrorCode.GuestPermittedViewNotFound && ServerSettingsUnauthenticated.isGuestEnabled() && !this.server.isSessionSiteOrServerAdmin()) {
      this.vizService.redirectToEmbeddedMode(false);
    }
    else {
      displayInvalidVizError(errorCode, newPath);
    }
  }

  private handleNewViewInfoReact(viewInfo: IViewDetailInfo, vizViewProps: VizViewerProps, searchParams: any) {
    this.loadNewViewInfoReact(viewInfo, vizViewProps);
    if (vizViewProps.displayRoutingError && !vizViewProps.displayDataAlertRoutingError) {
      // We display routing error when we could not fetch the view. Since we could fetch it now,
      // reset the error codes and  update the state since fetching this view was successful.
      // If we displayed a data alert error, that means we fetched the view correctly before.
      vizViewProps.displayRoutingError = false;
      vizViewProps.updateVizProviderState({
        ...initialNoErrorState,
        viewInfo: viewInfo
      });
    }
    // because the viewInfo can return after we have navigated away from this page, we only want to call
    // enterSpace if we are still on the view page. Otherwise, all sorts of errors happen if the SpaceInfo
    // is wrong- TFSID 956506
    if (isCurrentUrlViz()) {
      getAppStore().dispatch(enterSpace({
        item: viewInfo,
        spaceType: 'view',
        subspaceName: undefined
      }));
    }
    const alertId = searchParams[UrlParamKeys.EditDataAlert];
    //if fromEmail param in location.search then log the event
    if (searchParams[UrlParamKeys.FromFailureEmail]) {
      sendVisitAlertFromEmailTelemetry({
        alert_evaluation_state: 'Failure',
        alert_id: alertId,
        view_id: viewInfo.id,
        workbook_id: viewInfo.workbook.id,
        site_role: this.server.sessionSiteRole(),
      });
    }
    if (searchParams[UrlParamKeys.FromSuccessEmail]) {
      sendVisitAlertFromEmailTelemetry({
        alert_evaluation_state: 'Success',
        alert_id: alertId,
        view_id: viewInfo.id,
        workbook_id: viewInfo.workbook.id,
        site_role: this.server.sessionSiteRole(),
      });
    }
    maybeSendTelemetry_favoriteItemOpened(viewInfo);
  }

  private displayDataAlertError(dataAlertRouterErrorProps: RouterErrorOwnProps, viewInfo: IViewDetailInfo) {

    this.updateState({
      hasRoutingError: false,
      errorCode: undefined,
      errorVizPath: undefined,
      viewInfo: viewInfo,
      hasDataAlertError: true,
      dataAlertRouterErrorProps,
      dataAlert: undefined
    });
  }

  private handleGetDataAlertReturnsError(viewInfo: IViewDetailInfo): void {
    const viewUrl = this.props.urls.viewUrl(this.props.vizPathFromUrl);
    const routerErrorProps: RouterErrorOwnProps = {
      errorType: 'view-data-alert-exists',
      urls: this.props.urls,
      dataAlertErrorData: {
        viewUrl: viewUrl,
        owner: undefined
      }
    };
    this.displayDataAlertError(routerErrorProps, viewInfo);
  }

  private handleGetDataAlertResponse(dataAlert: IDataAlertInfo, viewInfo: IViewDetailInfo): void {
    //Using siteUrls.viewUrl($location.path()) would preserve query string, which won't work
    //So we're explicitly getting the view URL and explicity manually adding the hash
    const viewUrl = this.props.urls.viewUrl(this.props.vizPathFromUrl);
    //we don't want an alert error if there's a view error
    if (!!this.state.hasRoutingError && _.isUndefined(viewInfo)) {
      return;
    }
    if (!dataAlert) {
      const routerErrorProps: RouterErrorOwnProps = {
        errorType: 'view-data-alert-exists',
        urls: this.props.urls,
        dataAlertErrorData: {
          viewUrl: viewUrl,
          owner: undefined
        }
      };
      this.displayDataAlertError(routerErrorProps, viewInfo);
    }
    else if (dataAlert.owner.id !== this.server.sessionUserId() && !this.server.isSessionSiteOrServerAdmin()) {
      const routerErrorProps: RouterErrorOwnProps = {
        errorType: 'view-data-alert-permissions',
        urls: this.props.urls,
        dataAlertErrorData: {
          viewUrl: viewUrl,
          owner: dataAlert.owner
        }
      };
      this.displayDataAlertError(routerErrorProps, viewInfo);
    }
    else if (this.server.isViewerUser()) {
      const routerErrorProps: RouterErrorOwnProps = {
        errorType: 'view-data-alert-viewer',
        urls: this.props.urls,
        dataAlertErrorData: {
          viewUrl: viewUrl,
          owner: undefined
        }
      };
      this.displayDataAlertError(routerErrorProps, viewInfo);
    }
    else {
      // dataAlert successfully fetched and user has access to it-- make sure all errors are reset
      this.updateState({
        ...this.state,
        hasRoutingError: false,
        hasDataAlertError: false,
        dataAlertRouterErrorProps: undefined,
        viewInfo: viewInfo,
        dataAlert: dataAlert
      });
    }
  }

  private async updateBreadcrumbs(viewInfo: IViewDetailInfo) {
    let projectHierarchy: INamedItemInfo[] = [];

    // TODO:- Temporary fix for breadcrumbs will be updated in TFS 1210037
    if (viewInfo.workbook.project) {
      projectHierarchy = await Projects.getProjectHierarchy(viewInfo.workbook.project.id);
    }

    const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
    const siteUrls = this.props.urls;
    const workbook: IExplorableItemTypeInfo<'workbook'> = {
      id: viewInfo.workbook.id,
      name: viewInfo.workbook.name,
      contentType: 'workbook'
    };

    // because the project hierarchy can return after we have navigated away from this page, we only want to call
    // populateContentBreadcrumbs if we are still on the view page. Otherwise, the wrong breadcrumbs show.
    // TFSID 956506
    if (isCurrentUrlViz()) {
      populateContentBreadcrumbs([...projectHierarchyWithType, workbook, viewInfo], siteUrls);
    }
  }

  private async loadNewViewInfoReact(viewInfo: IViewDetailInfo, props: VizViewerProps): Promise<any> {
    BrowserTitleService.setVizTitle(viewInfo.name, viewInfo.workbook.name);
    props.vizProps.view = viewInfo;
    this.updateBreadcrumbs(viewInfo);
    Views.markRecentlyViewed(viewInfo.id);

    return Views.actions(viewInfo.id).then((actions: IViewActions) => {
      props.allowedAction = actions;
    });
  }

  private updateState = (newState: Pick<VizSpaceProviderState, keyof VizSpaceProviderState>): void => {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

const mapStateToProps: MapStateToProps<VizSpaceProviderStateProps> = (appState) => ({
  vizPath: selectVizPath(appState),
  sessionSiteUrlName: SessionSiteSelectors.siteUrlName(appState),
  layoutType: selectLayoutType(appState),
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(appState)
});

const mapDispatchToProps: MapDispatchToProps<VizSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setVizPath,
  setSpaceInfoItem,
}, dispatch);

export const ConnectedVizSpaceProvider: React.ComponentClass<VizSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(VizSpaceProvider);
